import React, { useEffect, useState, useRef } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import CommonContainerBox from "../custom/containers/CommonContainerBox";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import { getBillerPendingGridHeadings } from "../../constants/BillerManagementConstant";
import { useDispatch, useSelector } from "react-redux";
import ErrorMessage from "../custom/containers/ErrorMessage";
import actions from "../../actions";
import billerPendingApprovalsActionsType from "../../actions/types/billerPendingApproval.action.type";
import { ResponsiveBox, CircularProgress, Grid, Icon, Typography } from "../primitives";

const BillerPendingApprovals = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    location: { state },
  } = props;

  const [loading, setLoading] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const userRoleTypes = useSelector((state) => state.userRoleTypeReducer);

  const userDetails = useSelector((state) => state.loginUserReducer?.user);
  const userRole = userDetails?.role?.role_type;
  const selectedFiNumber = useSelector((state) => state.fiReducer?.selectedFi);
  
  const billerApprovalReducer = useSelector( (state) => state.billerApprovalReducer );
  const billerPendingData = billerApprovalReducer?.billerPendingApprovalData;
  const billerPendingError = billerApprovalReducer?.billerPendingApprovalError;

  const errorRef = useRef();
  const [roleData, setRoleData] = useState(null);

  const [searchValues, setSearchValues] = useState(
        { userId: userDetails.id,
          languageCode: userDetails.languageCode || "E",
          fiNumber: userDetails.fiNumber,
          selectedFiNumber: selectedFiNumber,
          offset: 0,
          limit: 100
        }
  );

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.focus();
    }
  }, [billerPendingError]);

  useEffect(() => {
    if (state?.fromDetailPage && billerApprovalReducer.searchValues) { // back from details
      // transfer into searchValues the attributes saved in rejectTransactionResponse.transactionForRejectDetails
      Object.keys(billerApprovalReducer.searchValues).forEach(key => searchValues[key] = billerApprovalReducer.searchValues[key]);

      // restore pagination settings:  searchValues has already been updated from recallPendingApprovalsDetails during the initialization 
      setPageNumber(searchValues.offset / searchValues.limit);
      loadResults();
      state.fromDetailPage = null;
    } else {  // initial load
      // reset the search result list
      dispatch( { data: null, totalCount: 0, type: billerPendingApprovalsActionsType.GET_PENDING_BILLER_APPROVAL_SUCCESS } );
      // Load role types
      dispatch(
        actions.userActions.getUserRoleTypeList(
          userDetails.fiNumber,
          userDetails.role.role_type,
          "manageusercomponent",
          t,
          setRoleData,
          false
        )
      );
    }
    dispatch(actions.fiActions.getMasterFiDescriptionList(t));
  }, []);

  useEffect(() => {
    if (roleData) {
      searchValues.roleIdList = roleData;
      loadResults();
    }
  }, [roleData]);

  let loadResults = () => {
    // do we need to reset error message here ??

    setLoading(true);
    dispatch(
        actions.billerPendingApprovalsActions.getPendingBillerApprovals( searchValues, setLoading )
    );
  };

  const paginate = (values) => {
      searchValues.offset = values.startOffset;
      if (searchValues.limit !== (values.endOffset - values.startOffset)) {  // page size was changed
          searchValues.limit = values.endOffset - values.startOffset;
          setPageNumber(0);
      }
      loadResults();
  };

  return (
    <div role="main">
      <ErrorMessage 
        error={billerPendingError}
        errorRef={errorRef}
        mt={2}
      />

      <CommonContainerBox
        marginTop="30px"
        padding="0px 24px 0px 24px"
        minHeight="calc(100vh - 280px)"
        title={t("erps.pendingapprovals.page.title.", "Pending approvals")}
      >
        <Grid container>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography variant="h1">
              {t("erps.pendingapprovals.page.title.", "Pending approvals")}
            </Typography>
          </Grid>
          <ResponsiveBox mt={2}>
            <Grid item lg={11} md={11} sm={12} xs={12}>
              <Typography variant="body1">
                {t(
                  "erps.pendingapprovals.page.description",
                  "Click on a transaction ID, in the table, to view details for the pending approval and either accept or decline the change."
                )}
              </Typography>
            </Grid>
            <ResponsiveBox mt={3} marginBottom={1}>
              <Typography variant="body1">
                {(billerPendingData && !loading ? billerApprovalReducer.totalCount || 0 : 0) +
                  t("erps.pendingapprovals.page.description.pendingapprovals", " pending approvals")}
              </Typography>
            </ResponsiveBox>
            {billerPendingData && !loading ? (
              <ResponsiveBox>
                <ResponsiveDataGrid
                  data={{
                    rows: billerPendingData || [],
                    count: billerApprovalReducer.totalCount || "0",
                    columns: getBillerPendingGridHeadings(
                      userDetails.userId,
                      state.componentAccess
                    ),
                  }}
                  sumOfColumnWidth="true"
                  initialLoading={loading}
                  pageNumber={pageNumber}
                  setPage={setPageNumber}
                  rowPerPage={searchValues?.limit}
                  userRole={userRole}
                  hideFooterPagination={false}
                  onAnyPaginationAction={paginate}
                  tableLabelText={t("erps.pendingapprovals.user.page.title")}
                />
              </ResponsiveBox>
            ) : (
              <ResponsiveBox ml={40} mt={10} sx={{ display: "flex" }}>
                <CircularProgress />
              </ResponsiveBox>
            )}
          </ResponsiveBox>
        </Grid>
      </CommonContainerBox>
    </div>
  );
};
export default withSidebar(BillerPendingApprovals);
